import React, { useState, useEffect, useRef } from "react";
import cx from "classnames";
import { useOnClickOutside } from "usehooks-ts";
import { ExternalLinkIcon } from "assets";
import { useTranslation } from "i18n";
import { ImageLocation } from "models";
import FullScreenMediaViewer from "pages/Profile/components/common/FullScreenMediaViewer";
import IProfileViewPhoto from "models/ProfileView/ProfileViewPhoto";
import ThumbnailButton from "pages/Profile/components/GallerySection/ThumbnailButton";
import SlideButton from "pages/Profile/components/common/SlideButton";
import ProfileButton from "pages/Profile/components/common/ProfileButton";
import { getImageUrl } from "services/UrlBuilder";
import getImageAltText from "pages/Profile/utils/getImageAltText";

interface Props {
  onToggleFullScreenViewer?: () => void;
  onCloseFullScreenViewer?: (isOpen: boolean) => void;
  selectedIndex?: number;
  gallery: IProfileViewPhoto[];
}

const GalleryMediaViewer = ({
  onToggleFullScreenViewer,
  onCloseFullScreenViewer,
  selectedIndex = 0,
  gallery,
}: Props) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(selectedIndex);
  const contentRef = useRef<HTMLImageElement>(null);

  const moveForward = (prev: number) => (prev + 1) % gallery.length;

  const moveBackward = (prev: number) =>
    (prev - 1 + gallery.length) % gallery.length;

  const nextSlide = () => setCurrentIndex(moveForward);

  const prevSlide = () => setCurrentIndex(moveBackward);

  const selectedPhoto = gallery[currentIndex];
  const isNavVisible = gallery.length > 1;

  const handleBackgroundClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (target.closest(".slide-button") || target.closest("button")) {
      return;
    }

    onCloseFullScreenViewer?.(false);
  };

  useOnClickOutside(contentRef, handleBackgroundClick);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { key } = event;

      if (key === "Left" || key === "ArrowLeft") {
        setCurrentIndex(moveBackward);
      }

      if (key === "Right" || key === "ArrowRight") {
        setCurrentIndex(moveForward);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <FullScreenMediaViewer onToggle={onToggleFullScreenViewer}>
      <div className="flex min-h-screen flex-col items-center justify-center p-4 sm:p-8">
        <div className="relative flex w-full max-w-4xl flex-col items-center">
          <div className="relative flex h-[70vh] w-full items-center justify-center overflow-hidden">
            <div className="relative m-auto flex h-full w-content">
              <img
                ref={contentRef}
                src={getImageUrl(selectedPhoto.url, ImageLocation.ViewPhoto)}
                alt={getImageAltText({
                  t,
                  description: selectedPhoto?.description,
                  photographer: selectedPhoto?.photographer,
                })}
                className="max-h-full max-w-full object-contain"
              />
              <ProfileButton
                onClick={() =>
                  window.open(
                    getImageUrl(selectedPhoto.url, ImageLocation.ViewPhoto),
                    "_blank"
                  )
                }
                className="absolute bottom-2 right-2 !bg-white text-color-brand-one"
                aria-label={t("common:aria.label.openImageInNewTab")}
              >
                <ExternalLinkIcon
                  width={20}
                  height={20}
                  aria-hidden="true"
                  focusable="false"
                />
              </ProfileButton>

              <div className="absolute bottom-3 left-10 right-10 text-s text-white drop-shadow-profile">
                {selectedPhoto.photographer &&
                  `© ${selectedPhoto.photographer}`}
                {selectedPhoto.photographer &&
                  selectedPhoto.description &&
                  " - "}
                {selectedPhoto.description && `${selectedPhoto.description}`}
              </div>
            </div>
            <SlideButton
              className={cx(
                "absolute left-4 bg-white/80 text-color-brand-one hover:bg-white/90",
                {
                  hidden: !isNavVisible,
                }
              )}
              direction="prev"
              onClick={prevSlide}
            />
            <SlideButton
              className={cx(
                "absolute right-4 bg-white/80 text-color-brand-one hover:bg-white/90",
                {
                  hidden: !isNavVisible,
                }
              )}
              direction="next"
              onClick={nextSlide}
            />
          </div>

          <div className="no-scrollbar mt-6 flex flex-wrap gap-2">
            {gallery.map((photo, index) => {
              return (
                <ThumbnailButton
                  key={`photo-button-${index}`}
                  isSelected={index === currentIndex}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={t("common:aria.label.goToGalleryImage", {
                    index: index + 1,
                    total: gallery.length,
                  })}
                >
                  <img
                    src={getImageUrl(photo.url, ImageLocation.ViewPhoto)}
                    alt={getImageAltText({
                      t,
                      description: photo?.description,
                      photographer: photo?.photographer,
                    })}
                    className={cx(
                      "block h-full w-full rounded-[10px] object-cover"
                    )}
                  />
                </ThumbnailButton>
              );
            })}
          </div>
        </div>
      </div>
    </FullScreenMediaViewer>
  );
};

export default GalleryMediaViewer;
